'use client'

import { ChevronRightIcon } from '@heroicons/react/24/outline'
import { usePathname } from 'next/navigation'

import { Container } from '@src/components/Container/Container'
import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink'
import { useTopNav } from '@src/components/TopNavV3/TopNavContext'
import { TopNavTile } from '@src/components/TopNavV3/TopNavTile'
import { getUnlocalizedPath } from '@src/i18n/locale.util'
import { useTrackSelectNavCta } from '@src/utils/analytics.util'
import { cn } from '@src/utils/tailwind'

export const TopNavDesktopSlidingPanel = () => {
  const trackSelectNavCta = useTrackSelectNavCta()
  const pathname = usePathname()
  const { setExpanded, expandedMenu, isExpanded } = useTopNav()

  function isCurrentPage(href = '/') {
    try {
      const linkUrl = new URL(href, window.location.origin)
      return linkUrl.pathname === getUnlocalizedPath(pathname)
    } catch (error) {
      return false
    }
  }

  const renderSubItems = () => {
    return expandedMenu?.subItems
      ?.filter((subItem) => Boolean(subItem.title))
      .map((subItem, index) => (
        <div key={index}>
          <div className="text-heading-5 mb-4">
            {subItem.href ? (
              <LocalizedLink
                onClick={() => {
                  trackSelectNavCta({ 'cta url': subItem?.href })
                  if (isCurrentPage(subItem.href)) setExpanded(false)
                }}
                className="inline-block hover:underline"
                href={subItem.href}
                rel={subItem.newTab ? 'noopener noreferrer' : ''}
                target={subItem.newTab ? '_blank' : '_self'}
              >
                {subItem.title}
              </LocalizedLink>
            ) : (
              <div>{subItem.title}</div>
            )}
          </div>
          <div className="flex flex-col">
            {subItem.links
              ?.filter((link) => Boolean(link.title))
              .map((link) => (
                <LocalizedLink
                  onClick={() => {
                    trackSelectNavCta({ 'cta url': link?.href })
                    if (isCurrentPage(link.href)) setExpanded(false)
                  }}
                  className="flex items-center gap-1 rounded-md p-2 font-medium text-bodi-gray-butters transition-all hover:bg-bodi-gray-dark hover:text-bodi-white"
                  key={link.title}
                  href={link.href ?? ''}
                  rel={link.newTab ? 'noopener noreferrer' : ''}
                  target={link.newTab ? '_blank' : '_self'}
                >
                  {link.title}
                  {link.stamp && (
                    <>
                      {' - '}
                      <span className="text-body-sm font-medium text-bodi-green-bright-500">
                        {link.stamp}
                      </span>
                    </>
                  )}
                </LocalizedLink>
              ))}
          </div>
        </div>
      ))
  }

  const renderTiles = () => {
    const tiles = (expandedMenu?.tiles ?? []).filter((tile) => tile.isVisible ?? true)
    if (tiles.length < 1) return null

    return tiles?.map((tile, i) => <TopNavTile tile={tile} key={i} />)
  }

  const numItems = expandedMenu?.subItems?.length ?? 0
  const numTiles = expandedMenu?.tiles?.length ?? 0

  return (
    <div
      onMouseLeave={() => setExpanded(false)}
      className={cn(
        'absolute top-full left-0 z-40 min-h-[467px] w-full border-t border-bodi-gray-dark bg-bodi-black-text bg-linear-to-t from-white/10 to-white/0 py-8 text-white transition-all duration-200',
        {
          'translate-y-0': isExpanded,
          '-translate-y-full': !isExpanded,
        }
      )}
    >
      <Container>
        {expandedMenu?.ctaDesktop?.title && (
          <div className="text-heading-display-3 mb-8 inline-flex items-center gap-2 py-3">
            {expandedMenu?.ctaDesktop?.href ? (
              <LocalizedLink
                className="hover:underline"
                href={expandedMenu?.ctaDesktop?.href ?? ''}
                rel={expandedMenu?.ctaDesktop?.newTab ? 'noopener noreferrer' : ''}
                target={expandedMenu?.ctaDesktop?.newTab ? '_blank' : '_self'}
                onClick={() => {
                  trackSelectNavCta({ 'cta url': expandedMenu?.ctaDesktop?.href })
                }}
              >
                {expandedMenu?.ctaDesktop?.title}
              </LocalizedLink>
            ) : (
              <div>{expandedMenu?.ctaDesktop?.title}</div>
            )}
            <ChevronRightIcon className="size-5 text-bodi-green-bright-500" />
          </div>
        )}

        <div className="grid w-full grid-cols-4 gap-10">
          <div
            className={cn('grid grid-cols-subgrid gap-10', {
              'col-span-3': (numTiles === 2 && numItems > 4) || numTiles < 2,
              'col-span-2': numTiles === 2 && numItems <= 4,
              'col-span-4': numTiles === 0,
            })}
          >
            {renderSubItems()}
          </div>

          <div
            className={cn('grid grid-cols-subgrid gap-10', {
              'col-span-2': numTiles === 2 && numItems <= 4,
              hidden: numTiles === 0,
            })}
          >
            {renderTiles()}
          </div>
        </div>
      </Container>
    </div>
  )
}
